import { useMemo, useState } from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DateInput } from '@/components/ui/date-input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

type PreviewTransaction = {
  id: number | null;
  order_name: string | null;
  order_id: string | number | null;
  line_item_id: string | number | null;
  order_at: string | null;
  payout_status: string | null;
  product: {
    name: string | null;
    sku: string | null;
    quantity: number;
  };
  pricing: {
    net_sales: number;
  };
  commission: {
    label: string | null;
    amount: number;
  };
};

type PreviewItem = {
  affiliator_id: string;
  app_user_id: string | number | null;
  auth_user_id: string | null;
  user_shopline_id: string | null;
  earn_code: string | null;
  display_name: string | null;
  email_address: string | null;
  total_unpaid_amount: number;
  transaction_count: number;
  net_sales: number;
  transactions: PreviewTransaction[];
};

type PreviewSummary = {
  payable_users_count: number;
  total_unpaid_amount: number;
  transaction_count: number;
  net_sales: number;
};

type PreviewResponse = {
  meta: {
    start_date: string;
    end_date: string;
    timezone: string;
  };
  summary: PreviewSummary;
  items: PreviewItem[];
};

type ExecuteItem = {
  affiliator_id: string;
  earn_code: string | null;
  display_name: string | null;
  user_shopline_id: string | null;
  total_unpaid_amount: number;
  transaction_count: number;
  success: boolean;
  status: string;
  detail: string | null;
};

type ExecuteResponse = {
  range: {
    start_date: string;
    end_date: string;
  };
  success_count: number;
  failure_count: number;
  total_payout_amount: number;
  items: ExecuteItem[];
};

type SessionHistoryRow = {
  executed_at: string;
  start_date: string;
  end_date: string;
  affiliator_id: string;
  earn_code: string | null;
  display_name: string | null;
  user_shopline_id: string | null;
  total_unpaid_amount: number;
  transaction_count: number;
  status: string;
};

function toNumber(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function parsePreviewTransaction(raw: unknown): PreviewTransaction {
  const row = (raw ?? {}) as Record<string, unknown>;
  const product = (row.product ?? {}) as Record<string, unknown>;
  const pricing = (row.pricing ?? {}) as Record<string, unknown>;
  const commission = (row.commission ?? {}) as Record<string, unknown>;
  return {
    id: Number.isFinite(Number(row.id)) ? Number(row.id) : null,
    order_name: row.order_name == null ? null : String(row.order_name),
    order_id: row.order_id == null ? null : (row.order_id as string | number),
    line_item_id: row.line_item_id == null ? null : (row.line_item_id as string | number),
    order_at: row.order_at == null ? null : String(row.order_at),
    payout_status: row.payout_status == null ? null : String(row.payout_status),
    product: {
      name: product.name == null ? null : String(product.name),
      sku: product.sku == null ? null : String(product.sku),
      quantity: toNumber(product.quantity, 0),
    },
    pricing: {
      net_sales: toNumber(pricing.net_sales, 0),
    },
    commission: {
      label: commission.label == null ? null : String(commission.label),
      amount: toNumber(commission.amount, 0),
    },
  };
}

function parsePreviewResponse(raw: unknown): PreviewResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const meta = (obj.meta ?? {}) as Record<string, unknown>;
  const summary = (obj.summary ?? {}) as Record<string, unknown>;
  const itemsRaw = Array.isArray(obj.items) ? obj.items : [];

  return {
    meta: {
      start_date: String(meta.start_date ?? ''),
      end_date: String(meta.end_date ?? ''),
      timezone: String(meta.timezone ?? 'Asia/Kuala_Lumpur'),
    },
    summary: {
      payable_users_count: toNumber(summary.payable_users_count, 0),
      total_unpaid_amount: toNumber(summary.total_unpaid_amount, 0),
      transaction_count: toNumber(summary.transaction_count, 0),
      net_sales: toNumber(summary.net_sales, 0),
    },
    items: itemsRaw.map((item) => {
      const row = item as Record<string, unknown>;
      return {
        affiliator_id: String(row.affiliator_id ?? ''),
        app_user_id: row.app_user_id == null ? null : (row.app_user_id as string | number),
        auth_user_id: row.auth_user_id == null ? null : String(row.auth_user_id),
        user_shopline_id: row.user_shopline_id == null ? null : String(row.user_shopline_id),
        earn_code: row.earn_code == null ? null : String(row.earn_code),
        display_name: row.display_name == null ? null : String(row.display_name),
        email_address: row.email_address == null ? null : String(row.email_address),
        total_unpaid_amount: toNumber(row.total_unpaid_amount, 0),
        transaction_count: toNumber(row.transaction_count, 0),
        net_sales: toNumber(row.net_sales, 0),
        transactions: Array.isArray(row.transactions) ? row.transactions.map(parsePreviewTransaction) : [],
      } satisfies PreviewItem;
    }),
  };
}

function parseExecuteResponse(raw: unknown): ExecuteResponse {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const range = (obj.range ?? {}) as Record<string, unknown>;
  const itemsRaw = Array.isArray(obj.items) ? obj.items : [];
  return {
    range: {
      start_date: String(range.start_date ?? ''),
      end_date: String(range.end_date ?? ''),
    },
    success_count: toNumber(obj.success_count, 0),
    failure_count: toNumber(obj.failure_count, 0),
    total_payout_amount: toNumber(obj.total_payout_amount, 0),
    items: itemsRaw.map((item) => {
      const row = item as Record<string, unknown>;
      return {
        affiliator_id: String(row.affiliator_id ?? ''),
        earn_code: row.earn_code == null ? null : String(row.earn_code),
        display_name: row.display_name == null ? null : String(row.display_name),
        user_shopline_id: row.user_shopline_id == null ? null : String(row.user_shopline_id),
        total_unpaid_amount: toNumber(row.total_unpaid_amount, 0),
        transaction_count: toNumber(row.transaction_count, 0),
        success: Boolean(row.success),
        status: String(row.status ?? ''),
        detail: row.detail == null ? null : String(row.detail),
      } satisfies ExecuteItem;
    }),
  };
}

function downloadSessionHistoryCsv(rows: SessionHistoryRow[]) {
  const header = [
    'executed_at',
    'start_date',
    'end_date',
    'affiliator_id',
    'earn_code',
    'display_name',
    'user_shopline_id',
    'total_unpaid_amount',
    'transaction_count',
    'status',
  ];
  const escapeCell = (value: string | number | null) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [
    header.join(','),
    ...rows.map((row) =>
      [
        row.executed_at,
        row.start_date,
        row.end_date,
        row.affiliator_id,
        row.earn_code,
        row.display_name,
        row.user_shopline_id,
        row.total_unpaid_amount.toFixed(2),
        row.transaction_count,
        row.status,
      ]
        .map(escapeCell)
        .join(',')
    ),
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `affiliate_payout_session_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

export function AffiliatePayOutPage() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [executingIds, setExecutingIds] = useState<string[]>([]);
  const [detailRow, setDetailRow] = useState<PreviewItem | null>(null);
  const [historyRows, setHistoryRows] = useState<SessionHistoryRow[]>([]);

  const canPreview = useMemo(() => !!startDate && !!endDate && !previewLoading, [startDate, endDate, previewLoading]);
  const previewItems = preview?.items ?? [];
  const selectedPayableIds = selectedIds.filter((id) => {
    const row = previewItems.find((item) => item.affiliator_id === id);
    return Boolean(row?.user_shopline_id) && row.total_unpaid_amount > 0 && row.transaction_count > 0;
  });
  const allVisiblePayableIds = previewItems
    .filter((item) => item.user_shopline_id && item.total_unpaid_amount > 0 && item.transaction_count > 0)
    .map((item) => item.affiliator_id);
  const allVisibleSelected =
    allVisiblePayableIds.length > 0 && allVisiblePayableIds.every((id) => selectedIds.includes(id));

  async function loadPreview() {
    if (!canPreview) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/affiliate-reporter/payout/preview/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: startDate, end_date: endDate }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Preview failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const raw = (await resp.json()) as unknown;
      setPreview(parsePreviewResponse(raw));
      setSelectedIds([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load affiliate payout preview.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function executePayout(affiliatorIds: string[]) {
    if (!startDate || !endDate || affiliatorIds.length === 0 || executeLoading) return;
    setExecuteLoading(true);
    setExecutingIds(affiliatorIds);
    setError(null);
    try {
      const resp = await fetch('/v1/api/affiliate-reporter/payout/execute/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          affiliator_ids: affiliatorIds,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Payout failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const parsed = parseExecuteResponse((await resp.json()) as unknown);
      const successHistory = parsed.items
        .filter((item) => item.success)
        .map(
          (item) =>
            ({
              executed_at: new Date().toISOString(),
              start_date: parsed.range.start_date,
              end_date: parsed.range.end_date,
              affiliator_id: item.affiliator_id,
              earn_code: item.earn_code,
              display_name: item.display_name,
              user_shopline_id: item.user_shopline_id,
              total_unpaid_amount: item.total_unpaid_amount,
              transaction_count: item.transaction_count,
              status: item.status,
            }) satisfies SessionHistoryRow
        );
      if (successHistory.length > 0) {
        setHistoryRows((current) => [...successHistory, ...current]);
      }
      if (parsed.failure_count > 0) {
        const failureDetails = parsed.items
          .filter((item) => !item.success)
          .map((item) => `${item.display_name || item.affiliator_id}: ${item.detail || item.status}`)
          .join(' | ');
        setError(failureDetails || 'Some payouts failed.');
      }
      await loadPreview();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute affiliate payout.');
    } finally {
      setExecuteLoading(false);
      setExecutingIds([]);
    }
  }

  function toggleSelected(affiliatorId: string, checked: boolean) {
    setSelectedIds((current) => {
      if (checked) {
        return current.includes(affiliatorId) ? current : [...current, affiliatorId];
      }
      return current.filter((value) => value !== affiliatorId);
    });
  }

  function toggleSelectAll(checked: boolean) {
    setSelectedIds(checked ? allVisiblePayableIds : []);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Affiliate Pay-Out</CardTitle>
          <CardDescription>
            Preview unpaid affiliate earnings by +08 date range, allocate Shopline store credit, then release the matching affiliate rows.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            <DateInput label="Start Date" value={startDate} onChange={(e) => setStartDate(e.currentTarget.value)} />
            <DateInput label="End Date" value={endDate} onChange={(e) => setEndDate(e.currentTarget.value)} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void loadPreview()} disabled={!canPreview}>
              {previewLoading ? 'Generating...' : 'Generate Affiliate Transaction Payout'}
            </Button>
            <Button
              variant="outline"
              onClick={() => void executePayout(selectedPayableIds)}
              disabled={selectedPayableIds.length === 0 || executeLoading || previewLoading}
            >
              {executeLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Pay Out Selected
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadSessionHistoryCsv(historyRows)}
              disabled={historyRows.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Session History CSV
            </Button>
          </div>
          {error ? (
            <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{error}</span>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {preview ? (
        <Card>
          <CardHeader>
            <CardTitle>Pay-Out Preview</CardTitle>
            <CardDescription>
              {preview.summary.payable_users_count} payable user(s), {preview.summary.transaction_count} transaction(s),{' '}
              {formatCurrency(preview.summary.total_unpaid_amount)} unpaid total. Timezone: {preview.meta.timezone}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Payable Users</div>
                <div className="mt-1 text-xl font-semibold">{preview.summary.payable_users_count}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Unpaid Amount</div>
                <div className="mt-1 text-xl font-semibold">{formatCurrency(preview.summary.total_unpaid_amount)}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Transactions</div>
                <div className="mt-1 text-xl font-semibold">{preview.summary.transaction_count}</div>
              </div>
              <div className="rounded-lg border p-3">
                <div className="text-xs text-muted-foreground">Net Sales</div>
                <div className="mt-1 text-xl font-semibold">{formatCurrency(preview.summary.net_sales)}</div>
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[48px]">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={(e) => toggleSelectAll(e.currentTarget.checked)}
                        aria-label="Select all payable affiliates"
                      />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Earn Code</TableHead>
                    <TableHead>Shopline ID</TableHead>
                    <TableHead className="text-right">Transactions</TableHead>
                    <TableHead className="text-right">Net Sales</TableHead>
                    <TableHead className="text-right">Unpaid Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="py-8 text-center text-muted-foreground">
                        No unpaid affiliate rows found for the selected +08 date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    preview.items.map((item) => {
                      const isExecuting = executingIds.includes(item.affiliator_id);
                      const isPayable = Boolean(item.user_shopline_id) && item.total_unpaid_amount > 0 && item.transaction_count > 0;
                      return (
                        <TableRow key={item.affiliator_id}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(item.affiliator_id)}
                              disabled={!isPayable || executeLoading}
                              onChange={(e) => toggleSelected(item.affiliator_id, e.currentTarget.checked)}
                              aria-label={`Select ${item.display_name || item.affiliator_id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{item.display_name || item.affiliator_id}</div>
                            <div className="text-xs text-muted-foreground">{item.email_address || item.affiliator_id}</div>
                          </TableCell>
                          <TableCell className="font-mono">{item.earn_code || '-'}</TableCell>
                          <TableCell className="font-mono">{item.user_shopline_id || '-'}</TableCell>
                          <TableCell className="text-right">{item.transaction_count}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.net_sales)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.total_unpaid_amount)}</TableCell>
                          <TableCell>
                            <Badge variant={isPayable ? 'default' : 'secondary'}>
                              {isPayable ? 'Ready' : 'Missing Shopline ID'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button variant="outline" size="sm" onClick={() => setDetailRow(item)}>
                                View Details
                              </Button>
                              <Button
                                size="sm"
                                disabled={!isPayable || executeLoading}
                                onClick={() => void executePayout([item.affiliator_id])}
                              >
                                {isExecuting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Pay Out
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle>Session History</CardTitle>
            <CardDescription>Successful payout activities appended during the current session.</CardDescription>
          </div>
          <Button variant="outline" onClick={() => downloadSessionHistoryCsv(historyRows)} disabled={historyRows.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Executed At</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Earn Code</TableHead>
                  <TableHead>Shopline ID</TableHead>
                  <TableHead className="text-right">Transactions</TableHead>
                  <TableHead className="text-right">Paid Out</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {historyRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No successful payout activities yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  historyRows.map((row) => (
                    <TableRow key={`${row.executed_at}-${row.affiliator_id}-${row.total_unpaid_amount}`}>
                      <TableCell>{formatDateTime(row.executed_at)}</TableCell>
                      <TableCell>{row.display_name || row.affiliator_id}</TableCell>
                      <TableCell className="font-mono">{row.earn_code || '-'}</TableCell>
                      <TableCell className="font-mono">{row.user_shopline_id || '-'}</TableCell>
                      <TableCell className="text-right">{row.transaction_count}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(row.total_unpaid_amount)}</TableCell>
                      <TableCell>
                        <Badge>{row.status}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailRow !== null} onOpenChange={(open) => (!open ? setDetailRow(null) : null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Affiliate Transaction Details</DialogTitle>
            <DialogDescription>
              {detailRow ? `${detailRow.display_name || detailRow.affiliator_id} · ${detailRow.transaction_count} transaction(s)` : 'Transaction details'}
            </DialogDescription>
          </DialogHeader>
          {!detailRow ? null : (
            <div className="max-h-[65vh] overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Order At</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Net Sales</TableHead>
                    <TableHead>Commission Type</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detailRow.transactions.map((transaction) => (
                    <TableRow key={`${transaction.id ?? 'na'}-${transaction.line_item_id ?? 'na'}`}>
                      <TableCell className="font-mono">{transaction.order_name || transaction.order_id || '-'}</TableCell>
                      <TableCell>{formatDateTime(transaction.order_at)}</TableCell>
                      <TableCell>{transaction.product.name || '-'}</TableCell>
                      <TableCell className="font-mono">{transaction.product.sku || '-'}</TableCell>
                      <TableCell className="text-right">{transaction.product.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(transaction.pricing.net_sales)}</TableCell>
                      <TableCell>{transaction.commission.label || '-'}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(transaction.commission.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
